import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  MapPin,
  ArrowRight,
  Compass,
  Heart,
  Sparkles,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { isAuthenticated, getStoredUser, API } from "@/lib/api";

export default function Landing() {
  const signedIn = isAuthenticated();
  const user = getStoredUser();
  const dashHref =
    signedIn && user && ["owner", "editor", "viewer"].includes(user.role)
      ? "/admin"
      : "/app";

  // CMS-driven menus. Fail silently so a backend outage never breaks the
  // landing page (the hardcoded "Admin" link + brand always render).
  const [headerLinks, setHeaderLinks] = useState([]);
  const [footerLinks, setFooterLinks] = useState([]);
  const [storeUrls, setStoreUrls] = useState({ play_store_url: "", apple_store_url: "" });
  useEffect(() => {
    axios
      .get(`${API}/pages?location=header&published_only=true`)
      .then((r) => setHeaderLinks(r.data || []))
      .catch(() => {});
    axios
      .get(`${API}/pages?location=footer&published_only=true`)
      .then((r) => setFooterLinks(r.data || []))
      .catch(() => {});
    axios
      .get(`${API}/public/app-store-urls`)
      .then((r) => setStoreUrls(r.data || {}))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-md shadow-primary/20">
              <MapPin className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="text-base font-extrabold tracking-tight">NearMe</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 mr-2">
            {headerLinks.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`header-link-${p.slug}`}
                className="px-3 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground rounded-lg transition-colors"
              >
                {p.menu_label || p.title}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {signedIn ? (
              <Button asChild data-testid="landing-continue-btn">
                <Link to={dashHref}>Continue <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" data-testid="landing-signin-btn">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button asChild data-testid="landing-signup-btn">
                  <Link to="/register">Get started</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-28 bg-grid">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-bold uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5" /> Discover · Save · Earn
          </span>
          <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05]">
            Everything you need, <span className="text-primary">right around you.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl">
            Restaurants, gyms, pharmacies, ATMs — with weather, directions and
            crowd-checked reviews. Save favorites and earn points every time you
            invite a friend.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {signedIn ? (
              <Button asChild size="lg" className="h-12 px-6 text-base">
                <Link to={dashHref}>Continue <ArrowRight className="w-5 h-5 ml-2" /></Link>
              </Button>
            ) : (
              <>
                <Button asChild size="lg" className="h-12 px-6 text-base" data-testid="hero-signup-btn">
                  <Link to="/register">Create free account <ArrowRight className="w-5 h-5 ml-2" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-12 px-6 text-base">
                  <Link to="/login">Sign in</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Feature icon={Compass} title="Discover" desc="500+ categories from restaurants to pharmacies, refreshed by Google Places." />
          <Feature icon={Heart} title="Save" desc="Bookmark places to a personal list, syncing across mobile and web." />
          <Feature icon={Sparkles} title="Earn" desc="Invite friends with your code — you both get 50 points each." />
        </div>
      </section>

      {/* Get the app */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="app-store-section">
        <div className="bg-card border border-border rounded-3xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            <div className="p-10 lg:p-14 flex flex-col justify-center">
              <span className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
                Get the app
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">
                NearMe in your pocket.
              </h2>
              <p className="text-base text-muted-foreground mb-8 max-w-md">
                Discover, save and share places on the go. Available on iOS and Android.
              </p>
              <div className="flex flex-wrap gap-3">
                <AppBadge
                  href={storeUrls.play_store_url}
                  storeName="Google Play"
                  primary="GET IT ON"
                  testId="play-store-badge"
                  Icon={PlayIcon}
                />
                <AppBadge
                  href={storeUrls.apple_store_url}
                  storeName="App Store"
                  primary="Download on the"
                  testId="apple-store-badge"
                  Icon={AppleIcon}
                />
              </div>
              {!storeUrls.play_store_url && !storeUrls.apple_store_url && (
                <p className="mt-4 text-xs text-muted-foreground italic">
                  Coming soon.
                </p>
              )}
            </div>
            <div className="relative bg-primary/5 min-h-[280px] md:min-h-full flex items-center justify-center p-10">
              <div className="w-36 h-72 rounded-[2rem] bg-background border-[6px] border-foreground/80 shadow-2xl relative overflow-hidden">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-foreground/80 rounded-full" />
                <div className="mt-8 px-3 space-y-2">
                  <div className="h-8 rounded-lg bg-primary/20" />
                  <div className="h-24 rounded-lg bg-accent/60" />
                  <div className="h-6 rounded-lg bg-muted" />
                  <div className="h-6 rounded-lg bg-muted" />
                  <div className="h-6 rounded-lg bg-muted" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>© 2026 NearMe. Built with care.</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {footerLinks.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`footer-link-${p.slug}`}
                className="hover:text-foreground"
              >
                {p.menu_label || p.title}
              </Link>
            ))}
            <Link to="/admin/login" data-testid="footer-admin-btn" className="hover:text-foreground inline-flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" /> Admin
            </Link>
            <span className="inline-flex items-center gap-1.5">
              <Smartphone className="w-4 h-4" /> iOS · Android
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="bg-card border border-border rounded-2xl p-6 hover:shadow-md hover:-translate-y-0.5 transition-all">
    <div className="w-10 h-10 rounded-xl bg-accent text-accent-foreground flex items-center justify-center mb-4">
      <Icon className="w-5 h-5" />
    </div>
    <div className="text-base font-bold mb-1.5">{title}</div>
    <div className="text-sm text-muted-foreground">{desc}</div>
  </div>
);

const AppBadge = ({ href, storeName, primary, Icon, testId }) => {
  const disabled = !href;
  const content = (
    <div
      className={`flex items-center gap-3 bg-foreground text-background rounded-xl px-4 py-3 shadow-md min-w-[180px] ${
        disabled ? "opacity-50 cursor-not-allowed" : "hover:shadow-lg hover:-translate-y-0.5 transition-all"
      }`}
    >
      <Icon className="w-8 h-8 shrink-0" />
      <div className="text-left leading-tight">
        <div className="text-[10px] font-medium opacity-80 uppercase tracking-wide">
          {primary}
        </div>
        <div className="text-base font-bold">{storeName}</div>
      </div>
    </div>
  );
  if (disabled)
    return (
      <div data-testid={`${testId}-disabled`} title="URL not configured">
        {content}
      </div>
    );
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testId}>
      {content}
    </a>
  );
};

const PlayIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M3.61 1.81L14.4 12 3.61 22.19a1.5 1.5 0 01-.61-1.19V3a1.5 1.5 0 01.61-1.19zM15.5 13.1l3.11 3.11-13.5 4.86 10.39-7.97zM19.62 16.2l-3.11-3.1-1.01-1.01 1.01-1.01 3.11-3.1a1.5 1.5 0 010 2.11L21 12l-1.38 1.38a1.5 1.5 0 010 2.83zM15.5 10.9L5.11 2.93l13.5 4.86-3.11 3.1z" />
  </svg>
);

const AppleIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);
